import { useState } from 'react';
import { T } from 'gt-react';
import { Action, Subject } from '@erp/authz';
import { useCan } from '@/hooks/useCan';
import { Badge } from 'src/components/ui/badge';
import { Button } from 'src/components/ui/button';
import {
  useDraftSubmission,
  type DraftPurchaseOrderSubmission as Submission,
} from 'src/api/rpc/hooks/procurement/usePurchaseOrderSubmission';
import { DraftSubmissionEditor } from './DraftSubmissionEditor';
import { creditTermsLabel } from './draftSubmissionForm';

/**
 * How a draft purchase order is sent to the vendor (GEN-2412): by EDI or by
 * email, the credit terms and the special instruction lines printed on the
 * PO. Until the PO is sent, a buyer who can edit purchase orders changes it
 * here; EDI can only be switched off.
 */
export function DraftPurchaseOrderSubmission({
  draftPurchaseOrderId,
}: {
  draftPurchaseOrderId: string;
}) {
  const [editing, setEditing] = useState(false);
  const [canUpdatePo] = useCan(Action.Update, Subject.PurchaseOrder);
  const submission = useDraftSubmission(draftPurchaseOrderId).data;
  if (!submission) return null;
  const canEdit = submission.editable && canUpdatePo;
  return (
    <section className="rounded-md border p-3 text-xs">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">
          <T context="draft PO section heading: how the PO is sent to the vendor">
            Sending
          </T>
        </h3>
        <div className="flex items-center gap-2">
          <SendByBadge submission={submission} />
          {canEdit && !editing ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setEditing(true)}
            >
              <T context="draft PO sending action">Change</T>
            </Button>
          ) : null}
        </div>
      </div>
      {editing ? (
        <DraftSubmissionEditor
          submission={submission}
          onDone={() => setEditing(false)}
        />
      ) : (
        <SubmissionSummary submission={submission} />
      )}
    </section>
  );
}

function SendByBadge({ submission }: { submission: Submission }) {
  if (submission.sendByEdi === null) return null;
  return submission.sendByEdi ? (
    <Badge variant="secondary">
      <T context="draft PO is sent to the vendor by EDI">EDI</T>
    </Badge>
  ) : (
    <Badge variant="outline">
      <T context="draft PO is sent to the vendor by email">Email</T>
    </Badge>
  );
}

function SubmissionSummary({ submission }: { submission: Submission }) {
  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-3">
      <Fact label={<T context="draft PO sending field: email recipient">To</T>}>
        {submission.emailTo}
      </Fact>
      <Fact label={<T context="draft PO sending field: email copy">Cc</T>}>
        {submission.emailCc}
      </Fact>
      <Fact label={<T context="draft PO sending field">Credit terms</T>}>
        {creditTermsLabel(
          submission.creditTermsCode,
          submission.creditTermsDescription,
        )}
      </Fact>
      <div className="col-span-full">
        <dt className="text-muted-foreground">
          <T context="draft PO sending field: email subject line">Subject</T>
        </dt>
        <dd>{submission.emailSubject}</dd>
      </div>
      <SpecialInstructions lines={submission.specialInstructions} />
      {submission.vendorEdi === null ? (
        <p className="text-muted-foreground col-span-full">
          <T context="draft PO sending: the vendor master has no row for this vendor">
            No vendor master setup on file for this vendor.
          </T>
        </p>
      ) : null}
    </dl>
  );
}

function Fact({
  label,
  children,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="break-all">{children}</dd>
    </div>
  );
}

function SpecialInstructions({
  lines,
}: {
  lines: Submission['specialInstructions'];
}) {
  if (lines.length === 0) return null;
  return (
    <div className="col-span-full">
      <dt className="text-muted-foreground">
        <T context="draft PO sending field: lines printed on the PO">
          Special instructions
        </T>
      </dt>
      <dd className="font-mono text-[11px] leading-tight whitespace-pre-line">
        {lines
          .map((line) =>
            line.code ? `${line.text} (${line.code})` : line.text,
          )
          .join('\n')}
      </dd>
    </div>
  );
}
